import { useEntitlements } from '@/lib/entitlements';
import { grupoDelMenu, type ModuleDefinition } from '@/lib/modules';
import { cn } from '@/lib/utils';
import { moduleIcon } from './module-icon';
import { NavItem } from './nav-item';
import { isNavigable } from './nav-targets';
import { useContadoresDelMenu } from './use-contadores-del-menu';

const GROUP_LABELS: Record<ModuleDefinition['group'], string> = {
  base: 'Principal',
  extra: 'Módulos adicionales',
  admin: 'Administración',
};

export interface SidebarNavProps {
  group: ModuleDefinition['group'];
  collapsed?: boolean;
  onNavigate?: () => void;
  className?: string;
}

/**
 * The nav for one sidebar group, derived from the enabled module set. Nothing
 * here is listed by hand: turning a module off in the plan removes its row, and
 * a module that has no screen yet never gets one (see `MODULES_WITHOUT_SCREEN`).
 */
export function SidebarNav({ group, collapsed = false, onNavigate, className }: SidebarNavProps) {
  const { navModules } = useEntitlements();
  const contadores = useContadoresDelMenu();
  const modules = navModules.filter((module) => grupoDelMenu(module) === group && isNavigable(module.id));
  if (modules.length === 0) return null;

  return (
    <nav aria-label={GROUP_LABELS[group]} className={className}>
      <ul className={cn('flex flex-col gap-0.5', collapsed && 'items-center')}>
        {modules.map((module) => (
          <li key={module.id} className={collapsed ? undefined : 'w-full'}>
            <NavItem
              to={module.path}
              label={module.label}
              icon={moduleIcon(module)}
              collapsed={collapsed}
              {...(contadores[module.id] !== undefined ? { count: contadores[module.id] } : {})}
              {...(onNavigate ? { onNavigate } : {})}
            />
          </li>
        ))}
      </ul>
    </nav>
  );
}
